import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, Heart, User, Menu, Phone, MapPin, ChevronDown } from "lucide-react";
import { BRAND, NAV_LINKS, whatsappLink } from "../mock/mock";
import { useCart } from "../context/CartContext";
import { useProducts } from "../context/ProductsContext";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { Input } from "./ui/input";
import { WhatsAppIcon } from "./WhatsAppFloat";

const Header = () => {
  const [q, setQ] = useState("");
  const [openSug, setOpenSug] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { totals, wishlist } = useCart();
  const { products } = useProducts();
  const nav = useNavigate();
  const loc = useLocation();
  const waHref = whatsappLink("Hello Agrawal Rich Dry Fruits, I'd like to place an order.");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpenSug(false); }, [loc.pathname]);

  const suggestions = q.trim().length > 0
    ? products.filter((p) => p.name.toLowerCase().includes(q.toLowerCase())).slice(0, 6)
    : [];

  const submitSearch = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    nav(`/shop?q=${encodeURIComponent(q.trim())}`);
    setOpenSug(false);
  };

  return (
    <header className={`sticky top-0 z-40 bg-cream/95 backdrop-blur-md transition-shadow ${scrolled ? "shadow-[0_6px_18px_-14px_rgba(139,26,26,0.35)]" : ""}`}>
      {/* Top row */}
      <div className="hidden md:flex items-center justify-between px-6 lg:px-10 py-1.5 text-[12px] text-[#6b4f39] border-b border-[#efe4c9]">
        <div className="flex items-center gap-4">
          <a href={`tel:${BRAND.phoneRaw}`} className="flex items-center gap-1.5 hover:text-brand transition-colors"><Phone className="h-3.5 w-3.5" /> {BRAND.phone}</a>
          <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {BRAND.addressShort}</span>
        </div>
        <div className="flex items-center gap-5">
          <a href={waHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#128C7E] hover:text-[#075E54] font-semibold transition-colors"><WhatsAppIcon className="h-3.5 w-3.5" /> WhatsApp Order</a>
          <Link to="/wholesale" className="hover:text-brand transition-colors">Bulk / Wholesale</Link>
          <Link to="/about" className="hover:text-brand transition-colors">Our Legacy</Link>
          <Link to="/contact" className="hover:text-brand transition-colors">Contact Us</Link>
          <span className="text-olive">Trusted Since 1973</span>
        </div>
      </div>

      {/* Middle row */}
      <div className="flex items-center gap-3 md:gap-6 px-4 md:px-6 lg:px-10 py-3">
        <Sheet>
          <SheetTrigger asChild>
            <button className="md:hidden p-2 -ml-2 text-[#3a2a20]" aria-label="Open menu">
              <Menu className="h-6 w-6" />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="bg-cream w-[85%] max-w-sm">
            <div className="flex items-center gap-3 pb-4 border-b border-[#efe4c9]">
              <img src={BRAND.logo} alt="" className="h-12 w-12 object-contain" />
              <div>
                <div className="font-serif-brand font-bold text-brand text-lg leading-tight">Agrawal Rich</div>
                <div className="text-[11px] tracking-[0.2em] text-olive">DRY FRUITS · SINCE 1973</div>
              </div>
            </div>
            <nav className="mt-4 flex flex-col">
              {NAV_LINKS.map((l) => (
                <Link key={l.label} to={l.to} className="py-2.5 border-b border-[#efe4c9] text-[15px] text-[#3a2a20] hover:text-brand">{l.label}</Link>
              ))}
              <Link to="/contact" className="py-2.5 border-b border-[#efe4c9] text-[15px] text-[#3a2a20] hover:text-brand">Contact</Link>
            </nav>
            <a href={waHref} target="_blank" rel="noopener noreferrer" className="mt-5 flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5b] text-white font-semibold py-2.5 rounded-lg"><WhatsAppIcon className="h-5 w-5" /> Order on WhatsApp</a>
            <div className="mt-4 text-sm text-[#6b4f39]">
              <a href={`tel:${BRAND.phoneRaw}`} className="flex items-center gap-2 hover:text-brand"><Phone className="h-4 w-4 text-brand" /> {BRAND.phone}</a>
              <div className="flex items-start gap-2 mt-2"><MapPin className="h-4 w-4 mt-0.5 text-brand" /> {BRAND.address}</div>
            </div>
          </SheetContent>
        </Sheet>

        <Link to="/" className="flex items-center gap-3 min-w-0">
          <img src={BRAND.logo} alt={BRAND.name} className="h-12 w-12 md:h-14 md:w-14 object-contain shrink-0" />
          <div className="leading-tight truncate">
            <div className="font-serif-brand font-bold text-brand text-[18px] md:text-[22px] truncate">Agrawal Rich</div>
            <div className="text-[10px] md:text-[11px] tracking-[0.25em] text-olive font-semibold">DRY FRUITS · SINCE 1973</div>
          </div>
        </Link>

        <form onSubmit={submitSearch} className="relative flex-1 max-w-2xl mx-auto hidden sm:block">
          <div className="flex items-center rounded-full border border-[#e3d3ad] bg-white overflow-hidden shadow-sm focus-within:border-brand transition-colors">
            <div className="hidden md:flex items-center gap-1 px-4 py-2.5 text-[13px] text-[#6b4f39] border-r border-[#efe4c9] cursor-pointer select-none">
              All Categories <ChevronDown className="h-4 w-4" />
            </div>
            <Input
              value={q}
              onChange={(e) => { setQ(e.target.value); setOpenSug(true); }}
              onFocus={() => setOpenSug(true)}
              placeholder="Search almonds, cashews, kesar, dates..."
              className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-10 md:h-11 bg-transparent px-4 text-[14px]"
            />
            <button type="submit" className="bg-brand text-white h-10 md:h-11 px-4 md:px-5 flex items-center justify-center hover:bg-[#6c1414] transition-colors">
              <Search className="h-4 w-4" />
            </button>
          </div>
          {openSug && suggestions.length > 0 && (
            <div className="absolute left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-[#efe4c9] overflow-hidden z-50">
              {suggestions.map((s) => (
                <button key={s.id} type="button" onClick={() => { nav(`/product/${s.id}`); setOpenSug(false); setQ(""); }} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[#faf3e2] transition-colors text-left">
                  <img src={s.image} alt="" className="h-10 w-10 rounded object-cover" />
                  <div className="min-w-0">
                    <div className="text-[14px] text-[#2a1a15] truncate">{s.name}</div>
                    <div className="text-[12px] text-[#6b4f39]">₹{s.price} · {s.weight}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </form>

        <div className="ml-auto flex items-center gap-1 md:gap-2">
          <a href={waHref} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 bg-[#25D366] hover:bg-[#1ebe5b] text-white text-[12.5px] font-semibold rounded-full transition-colors">
            <WhatsAppIcon className="h-4 w-4" /> <span className="hidden lg:inline">Order</span>
          </a>
          <button onClick={() => nav("/shop")} aria-label="Search" className="sm:hidden p-2 text-[#3a2a20]"><Search className="h-5 w-5" /></button>
          <Link to="/cart" className="hidden md:inline-flex relative items-center gap-1 px-3 py-2 text-[#3a2a20] hover:text-brand transition-colors" aria-label="Wishlist">
            <Heart className="h-5 w-5" />
            {wishlist.length > 0 && <span className="absolute -top-0.5 -right-0.5 bg-olive text-white text-[10px] font-bold rounded-full h-4 min-w-4 px-1 flex items-center justify-center">{wishlist.length}</span>}
          </Link>
          <Link to="/cart" className="relative inline-flex items-center gap-2 px-3 py-2 text-[#3a2a20] hover:text-brand transition-colors" aria-label="Cart">
            <ShoppingBag className="h-5 w-5" />
            <span className="hidden lg:inline text-[13px] font-medium">₹{totals.subtotal.toLocaleString("en-IN")}</span>
            {totals.count > 0 && <span className="absolute -top-0.5 right-0 bg-brand text-white text-[10px] font-bold rounded-full h-4 min-w-4 px-1 flex items-center justify-center">{totals.count}</span>}
          </Link>
        </div>
      </div>

      <nav className="hidden md:flex items-center justify-center gap-6 lg:gap-9 px-6 py-2.5 border-t border-[#efe4c9] bg-cream overflow-x-auto no-scrollbar">
        {NAV_LINKS.map((l) => (
          <Link key={l.label} to={l.to} className="whitespace-nowrap relative text-[13.5px] font-medium text-[#3a2a20] hover:text-brand transition-colors after:absolute after:left-0 after:right-0 after:-bottom-2 after:h-[2px] after:bg-brand after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:origin-left">
            {l.label}
          </Link>
        ))}
      </nav>
    </header>
  );
};

export default Header;
